import React, { useEffect, useState } from "react";
import { CalendarCheck, Mail, Paperclip, Users, UserRound, FileText, Plus, Pencil, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/apiClient";
import { PageHeader, Loader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const STATUS = { pending: "bg-amber-100 text-amber-700", paid: "bg-sky-100 text-sky-700", confirmed: "bg-green-100 text-green-700", cancelled: "bg-slate-100 text-slate-500" };

const fillName = (body, name) => (body || "").replace(/\{\{\s*name\s*\}\}/gi, name).replace(/\{\s*name\s*\}/gi, name);

function ReplyDialog({ booking, onClose }) {
  const [subject, setSubject] = useState("Your mentorship booking with Coach Reyland");
  const [message, setMessage] = useState(`Hi ${booking.name},\n\n`);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState("");
  useEffect(() => { api.get("/admin/templates").then((r) => setTemplates(r.data)).catch(() => {}); }, []);

  const applyTemplate = (id) => {
    setTpl(id);
    const t = templates.find((x) => x.id === id);
    if (t) setMessage(fillName(t.body, booking.name));
  };
  const send = async () => {
    if (!message.trim()) return toast.error("Write a message first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("subject", subject); fd.append("message", message);
      if (file) fd.append("file", file);
      await api.post(`/admin/mentorship/bookings/${booking.id}/reply`, fd);
      toast.success(`Reply sent to ${booking.name}'s inbox`); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="reply-dialog">
        <DialogHeader><DialogTitle>Reply to {booking.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Delivered to {booking.name}'s message inbox on the website ({booking.email}).</p>
          <div>
            <Label className="flex items-center gap-1.5"><FileText className="w-4 h-4" /> Use a saved reply template</Label>
            <Select value={tpl} onValueChange={applyTemplate}>
              <SelectTrigger data-testid="reply-template-select"><SelectValue placeholder="Choose a template (name fills in automatically)..." /></SelectTrigger>
              <SelectContent>
                {templates.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">No templates yet — create one with "Manage templates".</div>}
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.category} · {t.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="reply-subject" /></div>
          <div><Label>Message</Label><Textarea rows={7} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="reply-message" /></div>
          <div>
            <Label className="flex items-center gap-1.5"><Paperclip className="w-4 h-4" /> Attach a QR code or file</Label>
            <label className="flex-1 border border-border rounded-xl px-3 py-2 text-sm text-muted-foreground cursor-pointer truncate block mt-1" data-testid="reply-file-label">
              {file ? file.name : "Choose a file (optional)..."}
              <input type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="reply-file" />
            </label>
          </div>
        </div>
        <DialogFooter><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={send} disabled={busy} data-testid="reply-send"><Mail className="w-4 h-4 mr-1.5" /> Send Reply</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplatesDialog({ onClose }) {
  const [rows, setRows] = useState(null);
  const blank = { category: "General", title: "", body: "" };
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/templates").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) return toast.error("Title and message are required");
    try {
      if (editing) await api.put(`/admin/templates/${editing}`, form);
      else await api.post("/admin/templates", form);
      toast.success(editing ? "Template updated" : "Template saved");
      setForm(blank); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const edit = (t) => { setEditing(t.id); setForm({ category: t.category, title: t.title, body: t.body }); };
  const del = async (id) => { await api.delete(`/admin/templates/${id}`); if (editing === id) { setEditing(null); setForm(blank); } load(); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="templates-dialog">
        <DialogHeader><DialogTitle>Reply templates</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Use <code className="bg-secondary px-1 rounded">{"{{name}}"}</code> anywhere and the student's name fills in automatically when you pick the template.</p>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Category (e.g. Payment)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="tpl-category" />
            <Input placeholder="Title (e.g. Payment instructions)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="tpl-title" />
          </div>
          <Textarea rows={5} placeholder={"Hi {{name}}, here are your payment instructions..."} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="tpl-body" />
          <div className="flex gap-2">
            <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="tpl-save">{editing ? "Update template" : "Save template"}</Button>
            {editing && <Button variant="outline" className="rounded-full" onClick={() => { setEditing(null); setForm(blank); }} data-testid="tpl-cancel">Cancel edit</Button>}
          </div>
          <div className="border-t border-border pt-3 space-y-2">
            {!rows ? <Loader /> : rows.length === 0 ? <p className="text-sm text-muted-foreground text-center py-3">No templates yet.</p> : rows.map((t) => (
              <div key={t.id} className="flex items-start justify-between gap-2 border border-border rounded-xl p-3" data-testid={`tpl-row-${t.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{t.category} · {t.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-wrap">{t.body}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => edit(t)} className="text-teal" title="Edit" data-testid={`tpl-edit-${t.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(t.id)} className="text-red-500" title="Delete" data-testid={`tpl-del-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BookingSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/booking-settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const gp = s.geo_pricing || { group: {}, one: {} };
  const setGp = (type, tier, v) => setS({ ...s, geo_pricing: { ...gp, [type]: { ...(gp[type] || {}), [tier]: v } } });
  const save = async () => { await api.put("/admin/booking-settings", s); toast.success("Booking settings saved"); };
  return (
    <div className="bg-white border border-border rounded-2xl p-5 shadow-sm mb-6 space-y-4" data-testid="booking-settings">
      <p className="font-heading font-bold">Seats & Pricing</p>
      <div className="max-w-xs">
        <Label>Total group seats per session</Label>
        <Input type="number" min="0" value={s.total_seats} onChange={(e) => setS({ ...s, total_seats: parseInt(e.target.value) || 0 })} data-testid="seats-input" />
        <p className="text-xs text-muted-foreground mt-1">The homepage counter shows how many of these remain.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {["group", "one"].map((t) => (
          <div key={t} className="border border-border rounded-xl p-3">
            <p className="text-sm font-semibold capitalize mb-2">{t === "group" ? "Group" : "1-on-1"} pricing</p>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Philippines (PHP)</Label><Input value={(gp[t] || {}).ph || ""} onChange={(e) => setGp(t, "ph", e.target.value)} placeholder="₱499" data-testid={`price-${t}-ph`} /></div>
              <div><Label className="text-xs">International (USD)</Label><Input value={(gp[t] || {}).intl || ""} onChange={(e) => setGp(t, "intl", e.target.value)} placeholder="$29" data-testid={`price-${t}-intl`} /></div>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Visitors from the Philippines see the PHP price automatically; everyone else sees USD.</p>
      <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-booking-settings">Save Settings</Button>
    </div>
  );
}

export default function AdminBookings() {
  const [rows, setRows] = useState(null);
  const [reply, setReply] = useState(null);
  const [filter, setFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showTemplates, setShowTemplates] = useState(false);
  const load = async () => setRows((await api.get("/admin/mentorship/bookings")).data);
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.patch(`/admin/mentorship/bookings/${id}`, { status }); toast.success("Status updated"); load(); };
  if (!rows) return <Loader />;
  const shown = rows.filter((r) => (filter === "all" || r.status === filter) && (typeFilter === "all" || r.session_type === typeFilter));

  return (
    <div>
      <PageHeader title="Bookings Inbox" subtitle="Group and 1-on-1 mentorship booking threads — separate from the general Messages inbox" />
      <BookingSettings />
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-44" data-testid="booking-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="group">Group Mentorship</SelectItem>
            <SelectItem value="one">1-on-1 Mentorship</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44" data-testid="booking-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" className="rounded-full ml-auto" onClick={() => setShowTemplates(true)} data-testid="manage-templates-btn"><Plus className="w-4 h-4 mr-1.5" /> Manage templates</Button>
      </div>
      {shown.length === 0 ? <EmptyState icon={CalendarCheck} title="No bookings" description="Mentorship booking requests will appear here." testId="bookings-empty" /> : (
        <div className="space-y-3">
          {shown.map((b) => (
            <div key={b.id} className="bg-white border border-border rounded-2xl p-4 shadow-sm" data-testid={`booking-${b.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="font-medium flex items-center gap-1.5">
                    {b.session_type === "group" ? <Users className="w-4 h-4 text-teal" /> : <UserRound className="w-4 h-4 text-teal" />}
                    {b.name}
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${b.session_type === "group" ? "bg-teal/10 text-teal" : "bg-navy/10 text-navy"}`} data-testid={`booking-type-${b.id}`}>
                      {b.session_type === "group" ? "Group Mentorship" : "1-on-1 Mentorship"}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{b.email}{b.preferred_time ? ` · ${b.preferred_time}` : ""}</p>
                  {(b.experience_level || b.english_level) && (
                    <p className="text-xs mt-1 flex flex-wrap gap-2" data-testid={`booking-levels-${b.id}`}>
                      {b.experience_level && <span className="bg-secondary/60 px-2 py-0.5 rounded-full">Experience: {b.experience_level}</span>}
                      {b.english_level && <span className="bg-secondary/60 px-2 py-0.5 rounded-full">English: {b.english_level}</span>}
                    </p>
                  )}
                  {b.note && <p className="text-sm mt-1 p-2 rounded-lg bg-secondary/40">“{b.note}”</p>}
                  {(b.replies || []).length > 0 && <p className="text-xs text-teal mt-1">{b.replies.length} repl{b.replies.length === 1 ? "y" : "ies"} sent</p>}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS[b.status] || "bg-slate-100"}`}>{b.status}</span>
                  <Select value={b.status} onValueChange={(v) => setStatus(b.id, v)}>
                    <SelectTrigger className="w-32 h-8" data-testid={`booking-status-${b.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" className="rounded-full bg-teal hover:bg-teal/90" onClick={() => setReply(b)} data-testid={`booking-reply-${b.id}`}><Mail className="w-4 h-4 mr-1.5" /> Reply</Button>
                  {b.receipt_url && <a href={b.receipt_url.startsWith("http") ? b.receipt_url : process.env.REACT_APP_BACKEND_URL + b.receipt_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="rounded-full" data-testid={`booking-receipt-${b.id}`}>View receipt</Button></a>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {reply && <ReplyDialog booking={reply} onClose={() => setReply(null)} />}
      {showTemplates && <TemplatesDialog onClose={() => setShowTemplates(false)} />}
    </div>
  );
}
